import { twMerge } from 'tailwind-merge';
import { apiReadSandboxFile, apiWriteSandboxFile } from '~/lib/api/sandbox-files';
import type { SelectedElementPayload, StyleDeltas } from '~/lib/stores/element-selector';
import { deltasToClasses } from './tailwind-mapper';

/*
 * Deterministic edits: patch the source file in the sandbox directly, without
 * an agent turn. Every patcher is guarded — it only writes when it can prove
 * it found exactly the right spot (a unique literal match) — and reports
 * failure otherwise so the caller can fall back to the agent. The guards are
 * what make this safe: a fallback costs a normal agent turn, a mis-targeted
 * write would corrupt the user's site.
 */

export type DirectEditResult = { ok: true } | { ok: false; reason: string };

const fail = (reason: string): DirectEditResult => ({ ok: false, reason });

/** Indexes of every occurrence of `needle` in `haystack`. */
function occurrences(haystack: string, needle: string): number[] {
  const found: number[] = [];
  let idx = haystack.indexOf(needle);

  while (idx !== -1) {
    found.push(idx);
    idx = haystack.indexOf(needle, idx + 1);
  }

  return found;
}

function replaceAt(content: string, index: number, oldLen: number, next: string): string {
  return content.slice(0, index) + next + content.slice(index + oldLen);
}

/**
 * Insert a fresh `className="…"` on the element's opening tag at its reported
 * source location — used when the element has no className to anchor on, so
 * class-less elements can still be styled directly instead of the agent. Only
 * writes when it can locate the tag unambiguously and close to the reported
 * line; otherwise it fails and the caller falls back to the agent.
 */
async function insertClassNameDirect(
  chatId: string,
  sel: SelectedElementPayload,
  content: string,
  classes: string,
): Promise<DirectEditResult & { classes?: string }> {
  const lines = content.split('\n');

  if (sel.line < 1 || sel.line > lines.length) {
    return fail('source line out of range');
  }

  // Char offset of the start of the reported (1-indexed) line.
  let offset = 0;

  for (let i = 0; i < sel.line - 1; i++) {
    offset += lines[i].length + 1; // +1 for the '\n' split removed
  }

  const needle = `<${sel.tag}`;
  const idx = content.indexOf(needle, offset);

  if (idx === -1) {
    return fail('opening tag not found at source line');
  }

  // The JSX opening tag sits on (or just after) the reported line — a match far
  // past it is a different element, not ours.
  if (idx - offset > 400) {
    return fail('opening tag too far from source line');
  }

  // The char after the tag name must be a real boundary (space / newline / > /
  // self-close) so `<div` can't match `<divider`.
  if (!/[\s/>]/.test(content.charAt(idx + needle.length))) {
    return fail('tag name did not match cleanly');
  }

  try {
    await apiWriteSandboxFile(chatId, sel.file, replaceAt(content, idx + needle.length, 0, ` className="${classes}"`));
  } catch {
    return fail('could not write source file');
  }

  return { ok: true, classes };
}

/**
 * Style save: rewrite the element's className literal, merging the new
 * Tailwind classes over the old ones (tailwind-merge drops the conflicting
 * originals). Requires the runtime class attribute to appear verbatim as a
 * string-literal className exactly once in the file — that both locates the
 * element and proves the classes aren't computed at runtime. When the element
 * has no className at all, a fresh one is inserted at its source location.
 */
export async function applyStyleEditDirect(
  chatId: string,
  sel: SelectedElementPayload,
  deltas: StyleDeltas,
): Promise<DirectEditResult & { classes?: string }> {
  const mapped = deltasToClasses(deltas);

  if (!mapped.ok) {
    return fail(`no Tailwind mapping for ${mapped.unmappable}`);
  }

  let content: string;

  try {
    content = await apiReadSandboxFile(chatId, sel.file);
  } catch {
    return fail('could not read source file');
  }

  // No className to anchor on — insert one on the element's opening tag instead
  // of falling back to the agent (which would post a prompt into the chat).
  if (!sel.classes) {
    return insertClassNameDirect(chatId, sel, content, mapped.classes.join(' '));
  }

  const candidates = [`className="${sel.classes}"`, `className='${sel.classes}'`, `className={\`${sel.classes}\`}`];
  let matchNeedle: string | null = null;
  let matchIndex = -1;

  for (const needle of candidates) {
    const found = occurrences(content, needle);

    if (found.length > 1) {
      return fail('className is not unique in the file');
    }

    if (found.length === 1) {
      if (matchNeedle) {
        return fail('className is not unique in the file');
      }

      matchNeedle = needle;
      matchIndex = found[0];
    }
  }

  if (!matchNeedle) {
    return fail('className literal not found in source');
  }

  const merged = twMerge(sel.classes, mapped.classes.join(' '));
  const quote = matchNeedle.startsWith('className={`') ? '{`' : matchNeedle.charAt('className='.length);
  const next = quote === '{`' ? `className={\`${merged}\`}` : `className=${quote}${merged}${quote}`;

  try {
    await apiWriteSandboxFile(chatId, sel.file, replaceAt(content, matchIndex, matchNeedle.length, next));
  } catch {
    return fail('could not write source file');
  }

  // Return the className now in the file so the caller can re-anchor the
  // selection. Without this, a second style edit on the same element still
  // searches for the pre-edit className literal — which no longer exists after
  // this write — and spuriously reports a save failure.
  return { ok: true, classes: merged };
}

/** Characters that change meaning inside JSX text — edits containing them go to the agent. */
const JSX_UNSAFE = /[<>{}]/;

/**
 * Text edit: replace the element's text where the old text appears exactly
 * once in the file. Whitespace-normalized JSX (multi-line copy, entities,
 * interpolations) won't match — those fall back to the agent.
 */
export async function applyTextEditDirect(
  chatId: string,
  sel: SelectedElementPayload,
  oldText: string,
  newText: string,
): Promise<DirectEditResult> {
  const oldTrim = oldText.trim();
  const newTrim = newText.trim();

  if (!oldTrim || oldTrim.length > 1000) {
    return fail('old text empty or too long');
  }

  if (!newTrim || newTrim.length > 1000) {
    return fail('new text empty or too long');
  }

  if (JSX_UNSAFE.test(newTrim)) {
    return fail('new text contains JSX-significant characters');
  }

  let content: string;

  try {
    content = await apiReadSandboxFile(chatId, sel.file);
  } catch {
    return fail('could not read source file');
  }

  const found = occurrences(content, oldTrim);

  if (found.length !== 1) {
    return fail(found.length === 0 ? 'text not found verbatim in source' : 'text is not unique in the file');
  }

  try {
    await apiWriteSandboxFile(chatId, sel.file, replaceAt(content, found[0], oldTrim.length, newTrim));
  } catch {
    return fail('could not write source file');
  }

  return { ok: true };
}

/** Only plain http(s) or root-relative URLs, quote-free so the attribute can't break. */
const SAFE_URL = /^(https?:\/\/|\/)[^\s"'<>`]+$/;

/**
 * Image swap: replace the img's raw src attribute value where it appears
 * exactly once in the file. Imported/dynamic srcs won't match and fall back.
 */
export async function applyImageSwapDirect(
  chatId: string,
  sel: SelectedElementPayload,
  newUrl: string,
): Promise<DirectEditResult> {
  const oldSrc = sel.src;

  if (!oldSrc) {
    return fail('element has no raw src attribute');
  }

  if (!SAFE_URL.test(newUrl)) {
    return fail('replacement URL is not a plain http(s) URL');
  }

  let content: string;

  try {
    content = await apiReadSandboxFile(chatId, sel.file);
  } catch {
    return fail('could not read source file');
  }

  // Match the full quoted attribute value, not the bare URL — a src that is a
  // substring of another URL in the file would otherwise miscount.
  const candidates = [`"${oldSrc}"`, `'${oldSrc}'`];
  let matchNeedle: string | null = null;
  let matchIndex = -1;

  for (const needle of candidates) {
    const found = occurrences(content, needle);

    if (found.length > 1) {
      return fail('src is not unique in the file');
    }

    if (found.length === 1) {
      if (matchNeedle) {
        return fail('src is not unique in the file');
      }

      matchNeedle = needle;
      matchIndex = found[0];
    }
  }

  if (!matchNeedle) {
    return fail('src literal not found in source');
  }

  const quote = matchNeedle.charAt(0);

  try {
    await apiWriteSandboxFile(
      chatId,
      sel.file,
      replaceAt(content, matchIndex, matchNeedle.length, `${quote}${newUrl}${quote}`),
    );
  } catch {
    return fail('could not write source file');
  }

  return { ok: true };
}
